import React, { useEffect } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateFieldVerificationOfficialSize } from "services";

import { GET_FIELD_VERIFICATIONS, UPDATE_FIELD_VERIFICATION_OFFICIAL_SIZE } from "consts";

import { Button, Col, Form, InputNumber, Row, Message as message } from "components";

type FieldSizeVarianceCalculatorFormProps = {
  verificationId: string;
  platformSize: number;
  officialSize: number | null;
  farmId: string;
  onSuccess: () => void;
};

const calculateVariance = (officialSize: number | null, platformSize: number): number | null => {
  if (officialSize === null || platformSize === 0) {
    return null;
  }

  // Calculate how much larger or smaller the official size is compared to the platform size
  // Positive = official is larger, Negative = official is smaller
  return ((officialSize - platformSize) / platformSize) * 100;
};

const getMaxAllowedDeviation = (platformSize: number): number => {
  if (platformSize < 1) {
    return 10; // Field < 1 ha: Deviation max. +/-10%
  }
  if (platformSize >= 1 && platformSize <= 10) {
    return 5; // Field 1-10 ha: Deviation max. +/- 5%
  }
  return 1; // Field > 10 ha: Deviation max. +/- 1%
};

const isDeviationExcessive = (variance: number | null, platformSize: number): boolean => {
  if (variance === null) {
    return false;
  }
  const maxAllowedDeviation = getMaxAllowedDeviation(platformSize);
  return Math.abs(variance) > maxAllowedDeviation;
};

export const FieldSizeVarianceCalculatorForm = ({ verificationId, platformSize, officialSize, farmId, onSuccess }: FieldSizeVarianceCalculatorFormProps) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const watchedOfficialSize = Form.useWatch("officialSize", form);

  // Update form value when officialSize prop changes
  useEffect(() => {
    form.setFieldValue("officialSize", officialSize);
  }, [officialSize, form]);

  const { mutate, isPending } = useMutation({
    mutationKey: [UPDATE_FIELD_VERIFICATION_OFFICIAL_SIZE],
    mutationFn: updateFieldVerificationOfficialSize,
    onSuccess: () => {
      message({
        content: "Official field size saved",
        icon: "gremlin",
      });

      queryClient.invalidateQueries({ queryKey: [GET_FIELD_VERIFICATIONS, farmId] });

      onSuccess();
    },
  });

  const handleSubmit = (values: { officialSize: number | null }) => {
    mutate({
      verificationId,
      officialSize: values.officialSize,
    });
  };

  const variance = calculateVariance(watchedOfficialSize, platformSize);
  const deviationExcessive = isDeviationExcessive(variance, platformSize);
  const maxAllowedDeviation = getMaxAllowedDeviation(platformSize);

  const getVarianceDisplay = () => {
    if (variance === null) {
      return <span className="paragraph-m">Enter a size to calculate variance</span>;
    }

    const formattedVariance = variance.toFixed(2);
    const isLarger = variance > 0;
    const isEqual = variance === 0;

    if (isEqual) {
      return <span className="paragraph-m-bold">No difference (0%)</span>;
    }

    return (
      <span className={`paragraph-m-bold ${isLarger ? "red-text" : "green-text"}`}>
        {isLarger ? "+" : ""}
        {formattedVariance}% ({isLarger ? "larger" : "smaller"} than platform)
      </span>
    );
  };

  const getWarningMessage = () => {
    if (!deviationExcessive || variance === null) {
      return null;
    }

    const getFieldSizeCategory = () => {
      if (platformSize < 1) {
        return "fields < 1 ha";
      }
      if (platformSize <= 10) {
        return "fields 1-10 ha";
      }
      return "fields > 10 ha";
    };

    return (
      <div className="margin-top-100">
        <span className="paragraph-m-bold red-text">
          Warning: Deviation exceeds allowed threshold (±{maxAllowedDeviation}% for {getFieldSizeCategory()})
        </span>
      </div>
    );
  };

  return (
    <Form form={form} onFinish={handleSubmit}>
      <Row align="middle" gutter={[16, 16]}>
        <Col md={10}>
          <InputNumber name="officialSize" label="Agrar-Antrag size (ha)" min={0} step={0.01} initialValue={officialSize} />
        </Col>

        <Col md={10}>
          <div className="margin-top-150">{getVarianceDisplay()}</div>
          {getWarningMessage()}
        </Col>

        <Col md={4}>
          <div className="margin-top-150">
            <Button loading={isPending} type="primary" htmlType="submit">
              Save
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
};
